import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { client } from '../../client';
import type { ImageType } from '../../generated';

/*
 * Hand-written stand-in for GET /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}. The route is live behind
 * the API's `custom-benchmarks` flag, but the spec the generator reads does not list it yet. When it does, point
 * the fetcher at the generated SDK function and keep `toCustomBenchmark` as the one place the response is mapped;
 * callers only ever see `CustomBenchmark`. Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

const GET_BENCHMARK_URL = '/v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}';

/** A benchmark preview thumbnail as the API will serve it on the benchmark object. */
export interface BenchmarkPreviewView {
  id: string;
  url_64: string;
}

/** The benchmark as the API returns it (its `BenchmarkView`), kept to this seam and its mocks. */
export interface CustomBenchmarkResponse {
  id: string;
  kind: string;
  scope: ImageType;
  name: string | null;
  description: string | null;
  organization_id: string | null;
  workspace_id: string | null;
  created_at: string;
  updated_at: string;
  image_count: number;
  minimum_images: number;
  recommended_images: number;
  /** Collections scored against the benchmark; the API adds it with the benchmark collections listing. */
  collection_count?: number;
  /** Up to four thumbnails for the mosaic; absent until the API ships them. */
  previews?: BenchmarkPreviewView[];
}

/** A custom benchmark, mapped for the UI. */
export interface CustomBenchmark {
  id: string;
  name: string;
  description: string | null;
  /** Hero or Carousel, set at creation and locked afterwards. */
  scope: ImageType;
  imageCount: number;
  /** Images needed before collections can be scored against the benchmark. */
  minimumImages: number;
  /** Images the benchmark should have for stable scores. */
  recommendedImages: number;
  /** Collections scored against the benchmark; `undefined` until the API reports it. Not used yet. */
  collectionCount?: number;
  /** Thumbnail URLs for the mosaic, oldest image first; empty until the API ships them. */
  previewImageUrls: string[];
  createdAt: string;
  updatedAt: string;
}

export function toCustomBenchmark(response: CustomBenchmarkResponse): CustomBenchmark {
  return {
    id: response.id,
    name: response.name ?? '',
    description: response.description,
    scope: response.scope,
    imageCount: response.image_count,
    minimumImages: response.minimum_images,
    recommendedImages: response.recommended_images,
    collectionCount: response.collection_count,
    previewImageUrls: (response.previews ?? []).map((preview) => preview.url_64),
    createdAt: response.created_at,
    updatedAt: response.updated_at,
  };
}

export interface GetBenchmarkQueryVariables {
  workspace_id: string;
  benchmark_id: string;
}

export type GetBenchmarkQuery = CustomBenchmark;

async function getBenchmark({ workspace_id, benchmark_id }: GetBenchmarkQueryVariables) {
  const { data } = await client.get<{ 200: CustomBenchmarkResponse }, unknown, true>({
    url: GET_BENCHMARK_URL,
    path: { workspace_id, benchmark_id },
    throwOnError: true,
  });
  return toCustomBenchmark(data);
}

export const useGetBenchmarkQuery = <TData = GetBenchmarkQuery, TError = unknown>(
  variables: GetBenchmarkQueryVariables,
  options?: Omit<UseQueryOptions<GetBenchmarkQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetBenchmarkQuery, TError, TData>({
    queryKey: useGetBenchmarkQuery.getKey(variables),
    queryFn: () => getBenchmark(variables),
    ...options,
  });
};

useGetBenchmarkQuery.getKey = ({ workspace_id, benchmark_id }: GetBenchmarkQueryVariables) => [
  'GetBenchmark',
  { workspace_id, benchmark_id },
];

useGetBenchmarkQuery.fetcher = (variables: GetBenchmarkQueryVariables) => () => getBenchmark(variables);
